'use client'

import { useCallback } from 'react'
import { useRouter } from 'next/navigation'
import { useMessageCenterFeatureEnabled } from '@/contexts/AccountDashboardFeatureContext'
import { formatSupportPhoneForDisplay } from '@/lib/formatSupportPhoneForDisplay'
import { openSmsLink } from '@/lib/openSmsLink'
import { useGetTenantPhoneNumberQuery } from '@/lib/store/api/tenantIntegrationsApi'

const MESSAGE_CENTER_PATH = '/account/messages'
const SUPPORT_PHONE_PLACEHOLDER = '{phoneNumber}'
const PHONE_SUFFIX_PATTERN = /\s+at\s+\{phoneNumber\}/gi

type ContactSupportActionDestination = 'default' | 'message-center'

interface UseContactSupportOptions {
    defaultLabel: string
    defaultAction?: (openSms: () => void) => void | Promise<void>
    onAction?: (destination: ContactSupportActionDestination) => void
    smsHref?: string
}

const replaceCasePreserved = (text: string, pattern: RegExp, replacement: string): string => {
    return text.replace(pattern, (match) => {
        if (match === match.toUpperCase()) return replacement.toUpperCase()
        if (match[0] === match[0].toUpperCase()) {
            return replacement.charAt(0).toUpperCase() + replacement.slice(1)
        }
        return replacement.toLowerCase()
    })
}

const resolvePhoneInLabel = (text: string, phoneNumber: string | null | undefined): string => {
    if (phoneNumber) {
        return text.replaceAll(SUPPORT_PHONE_PLACEHOLDER, formatSupportPhoneForDisplay(phoneNumber))
    }
    return text.replace(PHONE_SUFFIX_PATTERN, '').replaceAll(SUPPORT_PHONE_PLACEHOLDER, '')
}

const adaptContactCopy = (text: string, messageCenterEnabled: boolean): string => {
    if (!messageCenterEnabled) return text

    let result = text.replace(PHONE_SUFFIX_PATTERN, '')
    result = replaceCasePreserved(result, /\btext our care team\b/gi, 'Message our care team')
    result = replaceCasePreserved(result, /\btext our support team\b/gi, 'Message our support team')
    result = replaceCasePreserved(result, /\btext your care team\b/gi, 'Message your care team')
    result = replaceCasePreserved(result, /\btext you\b/gi, 'Message you')
    result = replaceCasePreserved(result, /\btext us\b/gi, 'Message us')
    return result
}

/** Single entry point for every "contact support" action across the app. */
export const useContactSupport = ({
    defaultLabel,
    defaultAction = (openSms) => openSms(),
    onAction,
    smsHref,
}: UseContactSupportOptions) => {
    const messageCenterEnabled = useMessageCenterFeatureEnabled()
    const router = useRouter()
    const { data } = useGetTenantPhoneNumberQuery()
    const phoneNumber = data?.phone_number ?? null
    const resolvedSmsHref = smsHref ?? (phoneNumber ? `sms:${phoneNumber}` : undefined)
    const smsUnavailable = !messageCenterEnabled && !phoneNumber

    const openSms = useCallback(() => {
        openSmsLink(phoneNumber ?? undefined)()
    }, [phoneNumber])

    const contactUsAction = useCallback(() => {
        if (messageCenterEnabled) {
            router.push(MESSAGE_CENTER_PATH)
            onAction?.('message-center')
            return
        }
        if (smsUnavailable) return
        void defaultAction(openSms)
        onAction?.('default')
    }, [defaultAction, messageCenterEnabled, onAction, openSms, router, smsUnavailable])

    const contactUsAnchorProps = messageCenterEnabled
        ? { component: 'button' as const, type: 'button' as const, onClick: contactUsAction }
        : resolvedSmsHref
          ? { href: resolvedSmsHref }
          : {
                component: 'button' as const,
                type: 'button' as const,
                onClick: contactUsAction,
                disabled: true as const,
            }

    const contactUsLabel = messageCenterEnabled
        ? adaptContactCopy(defaultLabel, true)
        : resolvePhoneInLabel(defaultLabel, phoneNumber)

    return {
        contactUsLabel,
        contactUsAction,
        contactUsAnchorProps,
        /** Formatted for on-screen copy only; SMS actions keep the raw API value. */
        supportPhoneDisplay: phoneNumber ? formatSupportPhoneForDisplay(phoneNumber) : null,
    }
}
